/**
 * Itemized rate breakdown extracted from a UPS Rating (`RateResponse`) or Shipping
 * (`ShipmentResponse`) reply. Both share the same charge shapes, so one parser handles
 * either — a rate quote before processing, or the authoritative charges that come back
 * with the created shipment/label. Stored on a UpsShipment so the detail can be revisited
 * or exported later.
 */
export interface UpsRateCharge {
    /** UPS charge code (e.g. "375"), '' when none. */
    code: string;
    /** Human-readable label (UPS Description if given, else a code lookup/fallback). */
    label: string;
    /** Monetary amount as a string, e.g. "12.34". */
    amount: string;
}

export interface UpsRateBreakdown {
    currency: string;
    /** Base transportation charge. */
    transportation: string;
    /** Total service-options / accessorial charge. */
    serviceOptions: string;
    /** Each surcharge itemized (fuel, residential, delivery-area surcharge, etc.). */
    itemized: UpsRateCharge[];
    taxes: UpsRateCharge[];
    /** Grand total. */
    total: string;
    /** Billing weight UPS used, e.g. "5.0 LBS" ('' when not provided). */
    billingWeight: string;
}

export function emptyUpsRateBreakdown(): UpsRateBreakdown {
    return { currency: '', transportation: '', serviceOptions: '', itemized: [], taxes: [], total: '', billingWeight: '' };
}

/**
 * A single service's result from a UPS "Shop" rate (rates for all services in one call,
 * with time-in-transit). One per available service.
 */
export interface UpsServiceRate {
    serviceCode: string;
    /** Human label (from our config / UPS, falls back to the code). */
    serviceLabel: string;
    published: UpsRateBreakdown;
    /** Null when the response carried no negotiated pricing for this service. */
    negotiated: UpsRateBreakdown | null;
    /** Estimated delivery date as ISO yyyy-mm-dd ('' when not returned). */
    deliveryDate: string;
    /** Business days in transit ('' when not returned). */
    businessDays: string;
}
